import { useEffect, useMemo, useState } from "react";
import {
  CrmLead, CrmNotification, CrmTask, CLASSIFICATIONS, brl, createFollowUpSequence, dateTimeBR, daysSince,
  fetchLeads, fetchNotifications, fetchTasks, logEvent, onStageChanged, runCadenceAutomation, stageLabel, updateLead,
} from "@/lib/crm";
import { supabase } from "@/integrations/supabase/client";
import CrmDashboard from "./CrmDashboard";
import CrmPipeline from "./CrmPipeline";
import CrmAgenda from "./CrmAgenda";
import CrmLeadSheet from "./CrmLeadSheet";
import CrmAdmin from "@/components/admin/CrmAdmin";
import CrmNotifications from "./CrmNotifications";
import CrmIntelligence from "./CrmIntelligence";
import CrmErp from "./CrmErp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  Brain, Building2, CalendarDays, Inbox, LayoutDashboard, Plus, Search, Users, Workflow, Zap,
} from "lucide-react";


const db = supabase as any;

type View = "dashboard" | "pipeline" | "leads" | "ia" | "erp" | "agenda" | "formularios";

const NAV: { key: View; label: string; icon: React.ReactNode }[] = [
  { key: "dashboard", label: "Dashboard", icon: <LayoutDashboard className="h-4 w-4" /> },
  { key: "pipeline", label: "Pipeline", icon: <Workflow className="h-4 w-4" /> },
  { key: "leads", label: "Leads", icon: <Users className="h-4 w-4" /> },
  { key: "ia", label: "IA", icon: <Brain className="h-4 w-4" /> },
  { key: "erp", label: "ERP", icon: <Building2 className="h-4 w-4" /> },
  { key: "agenda", label: "Agenda", icon: <CalendarDays className="h-4 w-4" /> },
  { key: "formularios", label: "Formulários do site", icon: <Inbox className="h-4 w-4" /> },
];

const CrmShell = () => {
  const { toast } = useToast();
  const [view, setView] = useState<View>("dashboard");
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [notifications, setNotifications] = useState<CrmNotification[]>([]);
  const [openLead, setOpenLead] = useState<CrmLead | null>(null);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");

  const load = async () => {
    const [l, t, n] = await Promise.all([fetchLeads(), fetchTasks(), fetchNotifications()]);
    setLeads(l);
    setTasks(t);
    setNotifications(n);
    setOpenLead((cur) => (cur ? l.find((x) => x.id === cur.id) || null : null));
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel("crm_notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "crm_notifications" },
        () => {
          void load();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, []);

  const createLead = async () => {
    const name = newName.trim();
    if (!name) return;
    const { data, error } = await db
      .from("crm_leads")
      .insert({ client_name: name, client_phone: "Não informado", stage: "novo_lead", source: "Site" })
      .select("*")
      .single();
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    await logEvent(data.id, "lead_criado", "Lead criado manualmente");
    await createFollowUpSequence(data.id);
    setNewName("");
    toast({ title: "Lead criado com cadência de follow up" });
    await load();
    setOpenLead(data as CrmLead);
  };

  const moveStage = async (lead: CrmLead, stage: string) => {
    const { error } = await updateLead(lead.id, { stage, updated_at: new Date().toISOString() });
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    await logEvent(lead.id, "status", `Etapa alterada para ${stageLabel(stage)}`);
    await onStageChanged(lead.id, stage);
    await load();
  };

  const runAutomation = async () => {
    const created = await runCadenceAutomation(leads);
    toast({
      title: created > 0 ? `${created} lead(s) receberam próxima tarefa` : "Todos os leads já têm próxima tarefa",
    });
    await load();
  };


  const toggleTask = async (t: CrmTask) => {
    await db
      .from("crm_lead_tasks")
      .update({ is_done: !t.is_done, done_at: t.is_done ? null : new Date().toISOString() })
      .eq("id", t.id);
    await load();
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return leads;
    return leads.filter((l) =>
      [
        l.client_name, l.client_phone, l.client_whatsapp, l.client_email, l.cpf, l.neighborhood,
        l.source, l.campaign, l.classification, l.price_range, l.bedrooms, l.broker, l.city,
        l.property_name, l.general_notes, stageLabel(l.stage), ...(l.regions || []), ...(l.interests || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [leads, q]);

  const pendingToday = tasks.filter(
    (t) => !t.is_done && new Date(t.due_at).toDateString() === new Date().toDateString(),
  ).length;
  const late = tasks.filter((t) => !t.is_done && new Date(t.due_at) < new Date()).length;

  return (
    <div className="space-y-6">
      {/* Nav */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {NAV.map((n) => (
          <button
            key={n.key}
            type="button"
            onClick={() => setView(n.key)}
            className={`px-3 py-2 rounded-md text-sm font-body border transition-colors flex items-center gap-2 ${
              view === n.key
                ? "bg-accent text-accent-foreground border-accent font-semibold"
                : "bg-card text-muted-foreground border-border hover:border-accent/50"
            }`}
          >
            {n.icon}
            {n.label}
            {n.key === "agenda" && pendingToday > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground font-semibold">
                {pendingToday} hoje
              </span>
            )}
            {n.key === "dashboard" && late > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground font-semibold">
                {late} atrasada{late > 1 ? "s" : ""}
              </span>
            )}
          </button>
        ))}

        <div className="ml-auto flex items-center gap-2">
          <CrmNotifications
            notifications={notifications}
            leads={leads}
            onOpenLead={setOpenLead}
            onRefresh={load}
          />
          <Button size="sm" variant="outline" onClick={runAutomation}>
            <Zap className="h-4 w-4" /> Rodar automação
          </Button>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createLead()}
            placeholder="Nome do novo lead"
            className="font-body w-48 h-9"
          />
          <Button size="sm" variant="gold" onClick={createLead}>
            <Plus className="h-4 w-4" /> Novo lead
          </Button>
        </div>

      </div>

      {view === "dashboard" && (
        <CrmDashboard leads={leads} tasks={tasks} onOpenLead={setOpenLead} />
      )}

      {view === "pipeline" && (
        <CrmPipeline leads={leads} onOpenLead={setOpenLead} onMoveStage={moveStage} />
      )}

      {view === "agenda" && (
        <CrmAgenda leads={leads} tasks={tasks} onOpenLead={setOpenLead} onToggleTask={toggleTask} />
      )}

      {view === "leads" && (
        <div className="space-y-3">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Pesquisa avançada: nome, telefone, e-mail, CPF, bairro, origem, campanha, corretor, etapa…"
              className="pl-9 font-body"
            />
          </div>
          <p className="font-body text-xs text-muted-foreground">{filtered.length} lead(s)</p>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((l) => {
              const cls = CLASSIFICATIONS[l.classification] || CLASSIFICATIONS.morno;
              const openTasks = tasks.filter((t) => t.lead_id === l.id && !t.is_done).length;
              return (
                <Card
                  key={l.id}
                  onClick={() => setOpenLead(l)}
                  className="p-4 border border-border cursor-pointer hover:border-accent transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0">
                      <p className="font-display text-sm font-bold text-foreground truncate">{l.client_name}</p>
                      <p className="font-body text-xs text-muted-foreground truncate">
                        {l.client_phone} {l.client_email ? `· ${l.client_email}` : ""}
                      </p>
                    </div>
                    <Badge variant="outline" className={`ml-auto font-body text-[10px] ${cls.className}`}>
                      {cls.label}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 font-body text-[11px] text-muted-foreground">
                    <Badge variant="outline" className="font-body text-[10px]">{stageLabel(l.stage)}</Badge>
                    {l.source && <Badge variant="outline" className="font-body text-[10px]">{l.source}</Badge>}
                    {l.deal_value ? <Badge variant="outline" className="font-body text-[10px]">{brl(l.deal_value)}</Badge> : null}
                  </div>
                  <p className="mt-2 font-body text-[11px] text-muted-foreground">
                    Último contato: {daysSince(l.last_contact_at || l.created_at)} dia(s) ·{" "}
                    {openTasks > 0 ? `${openTasks} tarefa(s) aberta(s)` : "sem próxima tarefa"}
                    {l.follow_up_at ? ` · follow up ${dateTimeBR(l.follow_up_at)}` : ""}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {view === "ia" && (
        <CrmIntelligence leads={leads} onOpenLead={setOpenLead} onRefresh={load} />
      )}

      {view === "erp" && <CrmErp leads={leads} />}

      {view === "formularios" && <CrmAdmin />}

      {openLead && (
        <CrmLeadSheet
          lead={openLead}
          open={!!openLead}
          onClose={() => setOpenLead(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default CrmShell;
